import type { PlaySession } from "@slot/client-engine";
import type { GameHistoryProvider } from "./GameHistoryProvider";
import type { GameHistoryData, GameHistoryEntry } from "./GameHistoryTypes";

/**
 * HTTP transport implementation of `GameHistoryProvider`.
 *
 * Calls `GET {baseUrl}{historyPath}?limit=<n>&beforeRoundId=<id>` and returns
 * the page the backend assembled from its `rounds` table. Read-only — the
 * endpoint has no wallet / state-transition side effects.
 *
 * Pure transport — no field rewriting. The backend's
 * `GameHistoryEntry.replayUrl` is the API path (`/api/replay/<roundId>`),
 * not a click-to-navigate URL into replay mode. `GameHistoryPresenter`
 * owns the format-for-display step and constructs the navigation URL
 * from `entry.roundId` at render time. See its `formatEntries()`.
 *
 * Pagination is driven by the presenter — one HTTP call per page. The first
 * call (`fetchInitialPage`) returns `betHistory` plus the bounded biggest-wins
 * arrays; subsequent calls (`fetchMoreBetHistory`) return only the next slice
 * of `betHistory`. The presenter terminates when a page is shorter than the
 * requested `pageSize`.
 *
 * Non-2xx responses throw, and so does a 2xx whose rows fail the shape check (a string gameName,
 * date and roundId; the bet, win and charge in whole minor units — decision 22's rule for money in a
 * reply). The presenter's catch shows the page's empty state.
 */
export interface BackendGameHistoryProviderConfiguration {
  /** e.g. "http://localhost:3000". Trailing slash tolerated. */
  baseUrl: string;

  /** e.g. "/api/history". */
  historyPath: string;

  /** Optional headers (auth, session). Called per request. */
  getRequestHeaders?: () => Record<string, string>;

  /** The boot's play session: the history is the player's, asked for with its bearer (decision 81). */
  playSession?: PlaySession | null;

  /** Default 10_000ms per page request. `AbortSignal.timeout`, armed through the body. */
  requestTimeoutMs?: number;
}

const DEFAULT_REQUEST_TIMEOUT_MS = 10_000;

export class BackendGameHistoryProvider implements GameHistoryProvider {
  private readonly configuration: BackendGameHistoryProviderConfiguration;

  public constructor(configuration: BackendGameHistoryProviderConfiguration) {
    this.configuration = configuration;
  }

  public async fetchInitialPage(input: { pageSize: number }): Promise<GameHistoryData> {
    return this.fetchPage({ limit: input.pageSize });
  }

  public async fetchMoreBetHistory(input: { beforeRoundId: string; pageSize: number }): Promise<GameHistoryEntry[]> {
    const page = await this.fetchPage({ limit: input.pageSize, beforeRoundId: input.beforeRoundId });
    return page.betHistory;
  }

  private async fetchPage(query: { limit: number; beforeRoundId?: string }): Promise<GameHistoryData> {
    const url = this.buildUrl(query);
    const timeoutMs = this.configuration.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;

    // The body is read under the same deadline: `fetch` resolves on the headers, and a body that
    // never arrives used to leave the history page on its spinner for good.
    let response: Response;
    let rawBody: string;
    try {
      response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(this.configuration.getRequestHeaders?.() ?? {}),
          ...(this.configuration.playSession?.authorizationHeader() ?? {}),
        },
        signal: AbortSignal.timeout(timeoutMs),
      });
      rawBody = await response.text();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw new Error(`BackendGameHistoryProvider: network error calling ${url}: ${message}`);
    }

    if (!response.ok) {
      const detail = this.extractRejectDetail(rawBody);
      throw new Error(
        `BackendGameHistoryProvider: ${url} returned ${response.status} ${response.statusText}${detail ? ` (${detail})` : ""}`
      );
    }

    let parsed: unknown;
    try {
      parsed = rawBody.length > 0 ? JSON.parse(rawBody) : undefined;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw new Error(
        `BackendGameHistoryProvider: failed to parse response body from ${url}: ${message}`
      );
    }

    if (!isShapedGameHistoryData(parsed)) {
      throw new Error(
        `BackendGameHistoryProvider: 2xx response from ${url} is not a GameHistoryData (the three arrays, every row with a string gameName / date / roundId and its bet, win and charge in whole minor units).`
      );
    }

    return parsed;
  }

  private buildUrl(query: { limit: number; beforeRoundId?: string }): string {
    const base = this.configuration.baseUrl.replace(/\/$/, "");
    const path = this.configuration.historyPath.startsWith("/")
      ? this.configuration.historyPath
      : `/${this.configuration.historyPath}`;

    const params = new URLSearchParams();
    params.set("limit", String(query.limit));
    if (query.beforeRoundId !== undefined) {
      params.set("beforeRoundId", query.beforeRoundId);
    }

    return `${base}${path}?${params.toString()}`;
  }

  private extractRejectDetail(rawBody: string): string | null {
    if (rawBody.length === 0) return null;
    try {
      const parsed: unknown = JSON.parse(rawBody);
      if (!isRecord(parsed)) return null;
      const metadata = (parsed as { responseMetadata?: unknown }).responseMetadata;
      if (!isRecord(metadata)) return null;
      const statusMessage = (metadata as { statusMessage?: unknown }).statusMessage;
      if (typeof statusMessage === "string" && statusMessage.length > 0) return statusMessage;
      return null;
    } catch {
      return null;
    }
  }
}

function isShapedGameHistoryData(value: unknown): value is GameHistoryData {
  if (!isRecord(value)) return false;
  const v = value as {
    betHistory?: unknown;
    biggestWinsAllGames?: unknown;
    biggestWinsCurrentGame?: unknown;
  };
  return (
    Array.isArray(v.betHistory) &&
    Array.isArray(v.biggestWinsAllGames) &&
    Array.isArray(v.biggestWinsCurrentGame) &&
    [...v.betHistory, ...v.biggestWinsAllGames, ...v.biggestWinsCurrentGame].every(isShapedGameHistoryEntry)
  );
}

/**
 * The fields the history page reads without a fallback. The amounts are whole minor units, the rule every round
 * reply's money follows (decision 22): `MoneyFormatter`'s `BigInt` throws on anything else.
 */
function isShapedGameHistoryEntry(value: unknown): boolean {
  if (!isRecord(value)) return false;
  const e = value as {
    gameName?: unknown;
    date?: unknown;
    roundId?: unknown;
    betAmountMinor?: unknown;
    winAmountMinor?: unknown;
    chargeAmountMinor?: unknown;
  };
  return (
    typeof e.gameName === "string" &&
    typeof e.date === "string" &&
    typeof e.roundId === "string" &&
    isWholeMinorUnits(e.betAmountMinor) &&
    isWholeMinorUnits(e.winAmountMinor) &&
    (e.chargeAmountMinor === undefined || isWholeMinorUnits(e.chargeAmountMinor))
  );
}

function isWholeMinorUnits(value: unknown): boolean {
  return typeof value === "string" && /^\d+$/.test(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
